//#region FACTORY - STORE PATHS

/**
 * Factory for generating Firestore store paths in a store-centric and chainable way.
 *
 * Root hierarchy: `stores/{storeId}/...`
 *
 * ---
 * ### Usage
 * ```ts
 * const storePaths = createStorePaths().setStoreId('M1ql75dm22Xcyc5Ri6ZY');
 *
 * storePaths.store();                        // → "stores/M1ql75dm22Xcyc5Ri6ZY"
 * storePaths.settings();                     // → "stores/M1ql75dm22Xcyc5Ri6ZY/settings"
 * storePaths.settingsOverrides();            // → "stores/M1ql75dm22Xcyc5Ri6ZY/settings/overrides"
 * storePaths.settingsPriceBands();           // → "stores/M1ql75dm22Xcyc5Ri6ZY/settings/price_bands"
 * storePaths.settingsAutoOrderExpiration();  // → "stores/M1ql75dm22Xcyc5Ri6ZY/settings/auto_order_expiration"
 * ```
 */
export const createStorePaths = (): {
  base: string;
  customPath(...segments: string[]): string;
  docsTypesenseKey(): string;
  setStoreId(store: string): ReturnType<typeof createStorePaths>;
  settings(): string;
  settingsAddItemRules(): string;
  settingsAdvancedFilter(filterId: string): string;
  settingsAdvancedFilterGroups(): string;
  settingsAutoOrderExpiration(): string;
  settingsAutoOrderProcessed(): string;
  settingsAutoOrderSupplierEmails(): string;
  settingsDisableStockItemRules(): string;
  settingsDoc(docName: string): string;
  settingsEnableStockItemRules(): string;
  settingsMultiplePriceChangeRules(): string;
  settingsOpenOrderSettings(): string;
  settingsOverrides(): string;
  settingsPriceBands(): string;
  settingsShelfTalkersSettings(): string;
  settingsStockEditThresholds(): string;
  settingsStockItemDeletionRules(): string;
  settingsStoreTags(): string;
  store(): string;
  stores(): string;
} => {
  let storeId: string | null = null;

  return {
    /** Base path for all stores */
    base: 'stores',

    // ────────────────────────────────
    // Store context
    // ────────────────────────────────

    /**
     * Set the current store ID for chained path construction.
     * @example createStorePaths().setStoreId('M1ql75...');
     */
    setStoreId(store: string): ReturnType<typeof createStorePaths> {
      storeId = store;
      return this;
    },

    /** Root segment for all stores: `stores` */
    stores(): string {
      return this.base;
    },

    /** Root store document: `stores/{storeId}` */
    store(): string {
      if (!storeId) throw new Error('Store ID not set');
      return `${this.stores()}/${storeId}`;
    },

    // ────────────────────────────────
    // Generic builders
    // ────────────────────────────────

    /** Build any custom path under the current store */
    customPath(...segments: string[]): string {
      return `${this.store()}/${segments.join('/')}`;
    },

    /** Root of `/settings` for the current store: `stores/{storeId}/settings` */
    settings(): string {
      return `${this.store()}/settings`;
    },

    /** Generic path for a specific settings document: `stores/{storeId}/settings/{docName}` */
    settingsDoc(docName: string): string {
      return `${this.settings()}/${docName}`;
    },

    // ────────────────────────────────
    // Docs / sub-collections
    // ────────────────────────────────

    /** `/stores/{storeId}/docs/typesense_key` */
    docsTypesenseKey(): string {
      return `${this.store()}/docs/typesense_key`;
    },

    // ────────────────────────────────
    // Settings roots
    // ────────────────────────────────

    /** `/stores/{storeId}/settings/advanced_filters/filters/{filterId}` */
    settingsAdvancedFilter(filterId: string): string {
      return `${this.settings()}/advanced_filters/filters/${filterId}`;
    },

    /** `/stores/{storeId}/settings/advanced_filters/filters` */
    settingsAdvancedFilterGroups(): string {
      return `${this.settings()}/advanced_filters/filters`;
    },

    // ────────────────────────────────
    // Settings documents (shortcuts)
    // ────────────────────────────────

    /** `/stores/{storeId}/settings/overrides` */
    settingsOverrides(): string {
      return this.settingsDoc('overrides');
    },

    /** `/stores/{storeId}/settings/auto_order_processed` */
    settingsAutoOrderProcessed(): string {
      return this.settingsDoc('auto_order_processed');
    },

    /** `/stores/{storeId}/settings/auto_order_expiration` */
    settingsAutoOrderExpiration(): string {
      return this.settingsDoc('auto_order_expiration');
    },

    /** `/stores/{storeId}/settings/disable_stock_item_rules` */
    settingsDisableStockItemRules(): string {
      return this.settingsDoc('disable_stock_item_rules');
    },

    /** `/stores/{storeId}/settings/enable_stock_item_rules` */
    settingsEnableStockItemRules(): string {
      return this.settingsDoc('enable_stock_item_rules');
    },

    /** `/stores/{storeId}/settings/open_order_settings` */
    settingsOpenOrderSettings(): string {
      return this.settingsDoc('open_order_settings');
    },

    /** `/stores/{storeId}/settings/price_bands` */
    settingsPriceBands(): string {
      return this.settingsDoc('price_bands');
    },

    /** `/stores/{storeId}/settings/multiple_price_change_rules` */
    settingsMultiplePriceChangeRules(): string {
      return this.settingsDoc('multiple_price_change_rules');
    },

    /** `/stores/{storeId}/settings/shelf_talkers_settings` */
    settingsShelfTalkersSettings(): string {
      return this.settingsDoc('shelf_talkers_settings');
    },

    /** `/stores/{storeId}/settings/stock_edit_thresholds` */
    settingsStockEditThresholds(): string {
      return this.settingsDoc('stock_edit_thresholds');
    },

    /** `/stores/{storeId}/settings/stock_item_deletion_rules` */
    settingsStockItemDeletionRules(): string {
      return this.settingsDoc('stock_item_deletion_rules');
    },

    /** `/stores/{storeId}/settings/store_tags` */
    settingsStoreTags(): string {
      return this.settingsDoc('store_tags');
    },

    /** `/stores/{storeId}/settings/add_item_rules` */
    settingsAddItemRules(): string {
      return this.settingsDoc('add_item_rules');
    },

    /** `/stores/{storeId}/settings/ao_supp_emails` */
    settingsAutoOrderSupplierEmails(): string {
      return this.settingsDoc('ao_supp_emails');
    },
  };
};

//#endregion
